// Import required dependencies
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

// Import components
import Modal from '../../../../components/modal';
import Select from '../../../../components/form/select'
import ButtonIcon from '../../../../components/ButtonIcon/ButtonIcon';
import Switch from '../../../../components/form/switch';

// Import styles
import styles from './style.module.scss';

// Import API functions
import { addAppBank, bankList } from '../../../../../api';

/**
 * AssignMSP component for assigning a Merchant Service Provider to an application
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls the visibility of the modal
 * @param {Function} props.onClose - Callback function to close the modal
 * @param {Object} props.appData - Application data containing id and bank_id
 * @param {Function} props.onSuccess - Optional callback function to refresh data after successful assignment
 * @returns {JSX.Element} AssignMSP component
 */
const AssignMSP = ({ open, onClose, appData, onSuccess }) => {

  const [options, setOptions] = useState([]);
  const [selectedMsp, setSelectedMsp] = useState(null);
  const [selectedMspId, setSelectedMspId] = useState(null);
  const [childApp, setChildApp] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Fetches the list of available banks/MSPs
   * @async
   * @returns {Promise<void>}
   */
  const getBankList = async () => {
    const response = await bankList({ id: appData.id });

    if (response.status) {
      setOptions(response.bank_list);
      if (appData?.bank_id) {
        const selectedBank = response.bank_list.find(bank => bank.value === appData.bank_id);
        setSelectedMsp(selectedBank || null);
      }
    }
  }

  // Fetch bank list on component mount
  useEffect(() => {
    getBankList();
  }, []);

  // Update state when appData changes
  useEffect(() => {
    if (appData) {
      if (appData.bank_id && options.length > 0) {
        const bankId = Number(appData.bank_id);
        const selectedBank = options.find(bank => Number(bank.value) === bankId);
        setSelectedMsp(selectedBank || null);
        setSelectedMspId(bankId);
      }
      if (appData.child_app !== undefined) {
        setChildApp(appData.child_app);
      }
    }
  }, [appData, options]);

  /**
   * Handles the assignment of MSP to the application
   * @async
   * @returns {Promise<void>}
   */
  const assignMsp = async () => {
    if(selectedMspId === null) {
      setErrors({ msp: 'Please select an MSP' });
      return;
    }

    setIsLoading(true);
    const toastId = toast.loading('Assigning MSP...');
    
    const data = {
      id: appData.id,
      bank_id: selectedMspId,
      child_app: childApp
    }

    const response = await addAppBank(data);
    setIsLoading(false);

    if (response.status) {
      toast.success('MSP assigned successfully', { id: toastId });
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } else {
      toast.error('Something went wrong', { id: toastId });
      setErrors({ msp: 'Something went wrong' });
    }
  }
 
  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      size="sm"
    >
      <div className={styles.container} >
        <h6 className={styles.title} >Assign MSP</h6>

        <Select
          options={options}
          label={"Select MSP"}
          search={true}
          onChange={(value) => {
            let selectedOption;
            
            if (typeof value === 'object' && value !== null) {
              selectedOption = value;
            } else {
              selectedOption = options.find(opt => opt.value === value);
            }
            
            setSelectedMsp(selectedOption);
            setSelectedMspId(selectedOption?.value || null);
            setErrors({});
          }}
          value={selectedMspId}
          key={`msp-select-${selectedMsp?.value}`}
          id={"msp"}
          name={"msp"}
        />

        {errors.msp && <p className={styles.error}>{errors.msp}</p>}

        <Switch 
          id={"msp_switch"}
          name={"msp_switch"}
          label={"Child App"}
          value={true}
          onChange={(e) => {
            setChildApp(e.target.checked)
            setErrors({})
          }}
          checked={childApp}
        />

        <div className={styles.footer} >
          <ButtonIcon
            textColor="#254FE2"
            onClick={onClose}
          >
            Cancel
          </ButtonIcon>

          <ButtonIcon
            bgColor={"#4C70EF"}
            textColor="#FFF"
            onClick={assignMsp}
            isLoading={isLoading}
            disabled={errors.length > 0 || isLoading}
          >
            Assign
          </ButtonIcon>
        </div>
      </div>
    </Modal>
  )
}

export default AssignMSP